/**
 * Thin wrapper around @radix-ui/react-tooltip.
 *
 * Usage:
 *   <Tooltip content="Hello">
 *     <button>...</button>
 *   </Tooltip>
 *
 * The `TooltipProvider` is included here so callers don't need to
 * set it up separately (the Provider is idempotent when nested).
 */
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { cn } from "./cn";

interface TooltipProps {
  /** Tooltip text content */
  content: React.ReactNode;
  /** The element that triggers the tooltip */
  children: React.ReactNode;
  /** Which side the tooltip appears (default: right) */
  side?: "top" | "right" | "bottom" | "left";
  /** px offset from the trigger (default: 8) */
  sideOffset?: number;
  /** Delay before showing, ms (default: 300) */
  delayDuration?: number;
  /**
   * When true, the tooltip is suppressed and children are rendered as-is.
   * Use this when the label is already visible (e.g. expanded sidebar).
   */
  disabled?: boolean;
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = "right",
  sideOffset = 8,
  delayDuration = 300,
  disabled = false,
  className,
}: TooltipProps) {
  // When disabled, skip the tooltip entirely and just render the child.
  if (disabled) return <>{children}</>;
  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={sideOffset}
            className={cn(
              "z-100 rounded-md bg-popover px-2.5 py-1.5",
              "text-xs font-medium text-popover-foreground shadow-md",
              "animate-in fade-in-0 zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2",
              "data-[side=top]:slide-in-from-bottom-2",
              className
            )}
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
